import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { useServices } from '@/context/ServiceContext'
import { Avatar } from '@/components/Avatar'
import { RoleBadge } from '@/components/RoleBadge'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import type { AppUser } from '@/domain/user'
import type { Organization } from '@/domain/organization'

export function LoginPage() {
  const { currentUser, login } = useAuth()
  const { users, organizations } = useServices()
  const navigate = useNavigate()

  const [demoUsers, setDemoUsers] = useState<AppUser[]>([])
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [pendingId, setPendingId] = useState<string | null>(null)

  useEffect(() => {
    Promise.all([users.list(), organizations.list()]).then(([u, o]) => {
      setDemoUsers(u)
      setOrgs(o)
      setLoading(false)
    })
  }, [users, organizations])

  useEffect(() => {
    if (currentUser) navigate('/dashboard', { replace: true })
  }, [currentUser, navigate])

  const orgName = (orgId: string | null) => orgs.find((o) => o.id === orgId)?.name ?? 'No organization'

  const handleSelect = async (userId: string) => {
    setPendingId(userId)
    await login(userId)
    navigate('/dashboard')
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="text-center">
        <span className="cyber-pill bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30">
          Zero-Cost Development Mode
        </span>
        <h1 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">Demo Login</h1>
        <p className="mx-auto mt-3 max-w-xl text-white/60">
          No password needed. Pick any of the six CyberTales Learning roles to explore the platform
          from that person's perspective — this stands in for real authentication until the
          CloudSecure Authentication Service is connected (P5).
        </p>
      </div>

      {loading ? (
        <LoadingSpinner label="Loading demo users…" />
      ) : (
        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {demoUsers.map((u) => (
            <button
              key={u.id}
              onClick={() => handleSelect(u.id)}
              disabled={pendingId !== null}
              className="cyber-panel flex items-center gap-4 p-5 text-left transition hover:border-teal-500/40 disabled:opacity-60"
            >
              <Avatar name={u.displayName} color={u.avatarColor} size={48} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display font-semibold text-white">{u.displayName}</p>
                <p className="truncate text-xs text-white/50">{orgName(u.orgId)}</p>
                <div className="mt-2">
                  <RoleBadge role={u.role} />
                </div>
              </div>
              <span className="text-sm font-medium text-teal-300">
                {pendingId === u.id ? 'Signing in…' : 'Continue →'}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
